/**
 * ChatItem component for displaying individual chat entries in the sidebar
 * Provides functionality for selection, favoriting, sharing, exporting, and deletion
 */
import React, { useState, useRef, useEffect, useLayoutEffect } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { BsChatLeftText } from 'react-icons/bs';
import { FiMoreHorizontal, FiTrash2 } from 'react-icons/fi';
import { ChatItemProps } from '../../interfaces/components/chatItem';
import { RiShare2Line } from 'react-icons/ri';
import { TbFileExport } from 'react-icons/tb';
import { GoStar, GoStarFill } from 'react-icons/go';

/**
 * Wrapper for the more options icon with conditional visibility
 */
const MoreIconWrapper = styled.div<{ alwaysVisible?: boolean }>`
  position: relative;
  display: flex;
  align-items: center;
  justify-content: center;
  opacity: ${({ alwaysVisible }) => (alwaysVisible ? 1 : 0)};
  transition: opacity 0.2s ease;
`;

/**
 * Container for an individual chat item
 * Changes appearance based on selection state
 */
const ChatItemContainer = styled(motion.li)<{
  isSelected?: boolean;
  keepHover?: boolean;
}>`
  position: relative;
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.75rem 1rem;
  margin: 0.2rem;
  border-radius: 10px;
  cursor: pointer;
  background: ${({ theme, isSelected, keepHover }) =>
    isSelected ? theme.text.light + '22' : keepHover ? theme.text.light + '11' : 'transparent'};
  transition: all 0.2s ease;

  &:hover {
    background: ${({ theme, isSelected }) => (isSelected ? theme.text.light + '22' : theme.text.light + '11')};
  }

  &:hover ${MoreIconWrapper} {
    opacity: 1 !important;
  }
`;

/**
 * Chat icon with visual differences based on selection state
 */
const ChatIcon = styled(BsChatLeftText)<{ isSelected?: boolean }>`
  min-width: 16px;
  opacity: ${({ isSelected }) => (isSelected ? '1' : '0.6')};
`;

/**
 * Chat title component with overflow handling
 * Uses different font weight and opacity based on selection
 */
const ChatTitle = styled.span<{ isSelected?: boolean }>`
  flex-grow: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-weight: ${({ isSelected }) => (isSelected ? '600' : '500')};
  font-size: ${({ theme }) => theme.text.s85};
  color: ${({ theme, isSelected }) => theme.text.light + (isSelected ? '' : '99')};
`;

/**
 * Dropdown menu with animation and responsive positioning
 */
const DropdownMenu = styled(motion.div)`
  position: fixed;
  background: ${({ theme }) => theme.dropdown.background};
  box-shadow: 0px 4px 8px rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  overflow: hidden;
  z-index: 10;
  padding: 4px;
  border: 0 solid #e3e3e3;
  box-sizing: border-box;
  transform-origin: top right;
  z-index: 1000;

  /* Mobile adjustments to prevent menu from being cut off */
  @media (max-width: 768px) {
    max-width: 90vw;
    right: 5vw;
    left: auto !important; /* Override inline styles on mobile */
    /* Ensure the menu appears above all other elements */
    z-index: 200;
  }
`;

/**
 * Individual dropdown menu item
 * Supports danger state for delete option
 */
const DropdownItem = styled.div<{ isDanger?: boolean }>`
  margin: 4px;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.8rem 1rem;
  cursor: pointer;
  border-radius: 0;
  transition:
    background 0.2s ease,
    border-radius 0.2s ease;
  &:hover {
    background: ${({ theme }) => theme.text.light + '11'};
    border-radius: 8px;
  }
  color: ${({ theme, isDanger }) => (isDanger ? theme.dropdown.delete : theme.dropdown.text)};
`;

/**
 * Container for dropdown item icons
 */
const DropdownItemIcon = styled.div`
  display: flex;
  align-items: center;
`;

/**
 * Label for dropdown menu items
 */
const DropdownItemLabel = styled.span`
  font-size: ${({ theme }) => theme.text.s8};
  white-space: nowrap;
`;

/**
 * Overlay to capture clicks outside the dropdown menu
 * Covers the entire viewport
 */
const Overlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  z-index: 199; /* Just below the dropdown (z-index:200) but above everything else */
`;

/**
 * ChatItem component for displaying an individual chat in the sidebar
 * Features a dropdown menu with options to favorite, share, export, and delete
 *
 * @param title - Chat title to display
 * @param isSelected - Whether this chat is currently selected
 * @param onClick - Handler for when the chat item is clicked
 * @param onFavourite - Handler for toggling favorite status
 * @param onShare - Handler for sharing the chat
 * @param onExport - Handler for exporting the chat
 * @param onDelete - Handler for deleting the chat
 * @param isFavorite - Whether the chat is currently favorited
 * @returns Chat item component with dropdown menu
 */
const ChatItem: React.FC<ChatItemProps> = ({
  title,
  isSelected,
  onClick,
  onFavourite,
  onShare,
  onExport,
  onDelete,
  isFavorite,
}) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [menuPos, setMenuPos] = useState<{ top: number; left: number }>({
    top: 0,
    left: 0,
  });
  const menuRef = useRef<HTMLDivElement>(null);
  const dotsRef = useRef<HTMLDivElement>(null);

  /**
   * Toggles the dropdown menu open/closed
   * Calculates initial position based on the more options button
   *
   * @param e - Mouse event
   */
  const toggleMenu = (e: React.MouseEvent) => {
    e.stopPropagation();
    // If already open, just close it.
    if (menuOpen) {
      setMenuOpen(false);
      return;
    }
    if (dotsRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      // Preliminary position below the dots.
      setMenuPos({ top: rect.bottom + 4, left: rect.left + rect.width / 2 });
      setMenuOpen(true);
    }
  };

  /**
   * Calculates and sets the optimal position for the dropdown menu
   * Adjusts for screen edges and available space
   */
  useLayoutEffect(() => {
    if (menuOpen && dotsRef.current && menuRef.current) {
      const rect = dotsRef.current.getBoundingClientRect();
      const menuHeight = menuRef.current.clientHeight;
      const menuWidth = menuRef.current.clientWidth;
      const spaceBelow = window.innerHeight - rect.bottom;
      const spaceRight = window.innerWidth - rect.left;

      // For mobile screens, position is handled by CSS
      if (window.innerWidth <= 768) {
        // Just set top position, left will be handled by CSS
        setMenuPos({
          top: Math.min(rect.bottom + 4, window.innerHeight - menuHeight - 10),
          left: 0, // This will be overridden by CSS
        });
        return;
      }

      // Vertical positioning logic
      let topPos = rect.bottom + 4;
      if (spaceBelow < menuHeight + 4 && rect.top > menuHeight + 4) {
        // Position above the dots if not enough space below
        topPos = rect.top - menuHeight - 4;
      }

      // Horizontal positioning logic
      let leftPos = rect.left + rect.width / 2;
      if (spaceRight < menuWidth / 2) {
        // Align to right edge if not enough space on right
        leftPos = window.innerWidth - menuWidth - 10;
      } else if (leftPos - menuWidth / 2 < 10) {
        // Align to left edge if not enough space on left
        leftPos = 10 + menuWidth / 2;
      }

      setMenuPos({
        top: topPos,
        left: leftPos,
      });
    }
  }, [menuOpen]);

  /**
   * Adds a click outside listener to close the dropdown
   * Acts as a backup to the overlay approach
   */
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  return (
    <ChatItemContainer
      isSelected={isSelected}
      keepHover={menuOpen}
      onClick={onClick}
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: 10 }}
      transition={{ duration: 0.3 }}
    >
      <ChatIcon size={16} isSelected={isSelected} />
      <ChatTitle isSelected={isSelected}>{title || 'Untitled'}</ChatTitle>
      <MoreIconWrapper ref={dotsRef} alwaysVisible={isSelected || menuOpen} onClick={toggleMenu}>
        <motion.div whileHover={{ scale: 1.1 }}>
          <FiMoreHorizontal size={16} />
        </motion.div>
        <AnimatePresence>
          {menuOpen && (
            <>
              {/* Overlay that covers the screen; clicking it closes the dropdown */}
              <Overlay
                onClick={(e) => {
                  e.stopPropagation();
                  setMenuOpen(false);
                }}
              />
              <DropdownMenu
                ref={menuRef}
                style={{ top: menuPos.top, left: menuPos.left }}
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0, scale: 0.95 }}
                transition={{ duration: 0.2 }}
              >
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onFavourite && onFavourite();
                    setMenuOpen(false);
                  }}
                >
                  <DropdownItemIcon>{isFavorite ? <GoStarFill size={18} /> : <GoStar size={18} />}</DropdownItemIcon>
                  <DropdownItemLabel>{isFavorite ? 'Remove from' : 'Add to'} Favorite</DropdownItemLabel>
                </DropdownItem>
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onShare && onShare();
                    setMenuOpen(false);
                  }}
                >
                  <DropdownItemIcon>
                    <RiShare2Line size={18} />
                  </DropdownItemIcon>
                  <DropdownItemLabel>Share</DropdownItemLabel>
                </DropdownItem>
                <DropdownItem
                  onClick={(e) => {
                    e.stopPropagation();
                    onExport && onExport();
                    setMenuOpen(false);
                  }}
                >
                  <DropdownItemIcon>
                    <TbFileExport size={18} />
                  </DropdownItemIcon>
                  <DropdownItemLabel>Export</DropdownItemLabel>
                </DropdownItem>
                <DropdownItem
                  isDanger
                  onClick={(e) => {
                    e.stopPropagation();
                    onDelete && onDelete();
                    setMenuOpen(false);
                  }}
                >
                  <DropdownItemIcon>
                    <FiTrash2 size={18} />
                  </DropdownItemIcon>
                  <DropdownItemLabel>Delete</DropdownItemLabel>
                </DropdownItem>
              </DropdownMenu>
            </>
          )}
        </AnimatePresence>
      </MoreIconWrapper>
    </ChatItemContainer>
  );
};

export default ChatItem;
